<script>
import helpers from '@/lib/helpers'
import GalleryEdit from './gallery-edit.vue'

/**
 * The shared photo galleries (the user menu's Photo Galleries): find one,
 * open it - in this same modal - to change it or its photos, copy its link,
 * or make a new one. Back returns to the list.
 */
export default {
  name: 'gallery-modal',
  components: {GalleryEdit},

  props: {
    /** open straight on this gallery (the global lookup) */
    id: {type: Number, default: null},
  },

  data() {
    return {
      q: '',
      // whose galleries: 'me' (the default), 'all', or a user id
      owner: 'me',
      // off (the default): the galleries still good, private ones included; on: only the ones past their date
      expired: false,
      // everyone with a gallery I can see, for the chooser: [{id, userid, galleries}]
      owners: [],
      rows: [],
      loading: false,
      page: 1,
      perPage: 10,
      total: 0,
      // the gallery open in the details view: {id} or {id: null} for a new one; null = the list
      selected: null,
    }
  },

  mounted() {
    if (this.id)
      this.selected = {id: this.id, name: null}
    this.load()
  },

  computed: {
    /** the owner chooser: mine, everyone's, then each person with galleries */
    ownerOptions() {
      return [
        {value: 'me', text: 'My galleries'},
        {value: 'all', text: "Everyone's galleries"},
        ...this.owners.map(o => ({value: String(o.id), text: o.userid + ' (' + o.galleries + ')'})),
      ]
    },
  },

  methods: {
    async load(page) {
      if (typeof page === 'number')
        this.page = page
      else
        this.page = 1   // a new search starts at the first page
      this.loading = true
      try {
        const r = await this.asyncAction('/api/gallery', 'GET', {params: {q: this.q || undefined, owner: this.owner, expired: this.expired ? 1 : 0, page: this.page, per_page: this.perPage}, noload: true})
        this.rows = r.galleries
        this.total = r.total
        this.owners = r.owners || []
      } finally {
        this.loading = false
      }
    },
    open(row) {
      this.selected = row ? {id: row.id, name: row.name} : {id: null, name: null}
    },
    back() {
      this.selected = null
      this.load(this.page)
    },
    copy(row) {
      helpers.copy_to_clipboard(row.share_url)
    },
    status(row) {
      if (row.private)
        return {text: 'Private', variant: 'secondary'}
      if (row.expired)
        return {text: 'Expired', variant: 'warning'}
      return {text: row.expires ? 'Until ' + new Date(row.expires).toLocaleDateString() : 'Never expires', variant: 'success'}
    },
  },
}
</script>

<template>
  <modal :title="selected && !selected.id ? 'New Photo Gallery' : 'Photo Galleries'" height="600px" size="xl" dialog-class="gallery-dialog">
    <template #buttons>
      <b-button v-if="selected" variant="link" class="text-white" size="sm" @click="back">
        <font-awesome-icon :icon="['fas', 'arrow-left']" class="mr-1"/>BACK
      </b-button>
      <b-button v-else variant="link" class="text-white" size="sm" @click="open(null)">
        <font-awesome-icon :icon="['fas', 'plus']" class="mr-1"/>NEW GALLERY
      </b-button>
    </template>

    <!-- the details, in place of the list -->
    <gallery-edit v-if="selected" :key="selected.id || 'new'" :id="selected.id" @done="back"/>

    <template v-else>
    <!-- the search in the modal's top strip, fixed above the scrolling list -->
    <modal-portal to="top">
      <div class="d-flex align-items-center gallery-find">
        <b-form-input v-model="q" size="sm" placeholder="Find a gallery by name or GLI" debounce="300" autofocus @update="load"/>
        <!-- whose: opens on your own; pick everyone's or one person -->
        <b-form-select v-model="owner" :options="ownerOptions" size="sm" class="ml-2 w-auto flex-shrink-0" @change="load()"/>
        <!-- the ones past their date, instead of the ones still good -->
        <b-form-checkbox v-model="expired" switch size="sm" class="ml-3 flex-shrink-0 text-nowrap" @change="load()">Expired</b-form-checkbox>
      </div>
    </modal-portal>

    <div v-if="!loading && !rows.length" class="text-muted font-italic py-3 text-center">
      <template v-if="expired">No expired galleries here.</template>
      <template v-else-if="owner === 'me'">You have no shared galleries yet - make one from any photo's menu (Share) or with New Gallery, or pick Everyone's galleries above.</template>
      <template v-else>No shared galleries here.</template>
    </div>

    <!-- one block per gallery: the line (name, state, when, link), then its photos -->
    <b-list-group>
    <b-list-group-item v-for="item in rows" :key="item.id" class="gallery-row py-2 px-2" button @click="open(item)">
      <div class="d-flex align-items-center flex-wrap">
        <span class="text-muted mr-2">{{ item.gli }}</span>
        <strong v-if="item.name" class="mr-2">{{ item.name }}</strong>
        <b-badge :variant="status(item).variant" class="mr-1">{{ status(item).text }}</b-badge>
        <b-badge v-if="item.customer" variant="info" class="mr-1" :title="item.customer.company">Customer {{ item.customer.gli }}</b-badge>
        <b-badge v-else-if="item.vendor" variant="info" class="mr-1" :title="item.vendor.company">Vendor {{ item.vendor.gli }}</b-badge>
        <span class="small text-muted ml-auto text-nowrap">
          {{ item.photos_count }} photo{{ item.photos_count === 1 ? '' : 's' }}
          · <span :title="new Date(item.created).toLocaleString()">{{ item.created | timediff }}</span><template v-if="item.createdby"> by {{ item.createdby.userid }}</template>
        </span>
        <span class="ml-2 text-nowrap">
          <b-button size="sm" variant="link" class="p-0 px-1" title="Copy the link" @click.stop="copy(item)">
            <font-awesome-icon :icon="['far', 'copy']"/>
          </b-button>
          <b-button size="sm" variant="link" class="p-0 px-1" :href="item.share_url" target="_blank" title="Open the page" @click.stop>
            <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']"/>
          </b-button>
        </span>
      </div>
      <div v-if="(item.thumbs || []).length" class="d-flex flex-wrap align-items-end gallery-thumbs mt-1">
        <b-img v-for="(t, i) in item.thumbs" :key="i" :src="t" class="rounded border" width="64" height="64" style="object-fit: cover"/>
        <span v-if="item.photos_count > item.thumbs.length" class="small text-muted ml-1">+{{ item.photos_count - item.thumbs.length }} more</span>
      </div>
    </b-list-group-item>
    </b-list-group>

    <modal-portal to="footer" v-if="total > perPage">
      <div class="d-flex align-items-center">
        <small class="text-muted">{{ total }} galleries</small>
        <b-pagination :value="page" :total-rows="total" :per-page="perPage" size="sm" class="mb-0 ml-auto" @change="load"/>
      </div>
    </modal-portal>
    </template>
  </modal>
</template>

<style>
/* between Bootstrap's lg (800) and xl (1140) */
.modal-dialog.gallery-dialog {
  max-width: 1000px;
}
</style>

<style scoped>
.gallery-row {
  text-align: left;
}

.gallery-thumbs {
  gap: 4px;
}

.gallery-thumbs >>> img {
  background: #fff;
}
</style>
